import { useState, useMemo } from 'react';

import { AppIcon, AppIcons } from '@backrs_ui/common-ui';

import { Box, Typography, SxProps, Theme, Select, MenuItem } from '@mui/material';

export type FilterOption = Readonly<{
  key: string;
  label: string;
}>;

const validateSelections = (selectedValues: FilterOption[], options: FilterOption[]) => {
  const invalidKeys = selectedValues
    .filter((selected) => !options.some((option) => option.key === selected.key))
    .map((s) => s.key);

  if (invalidKeys.length > 0) {
    throw new Error(`Invalid selections: ${invalidKeys.join(', ')}`);
  }
};

type FilterButtonProps = Readonly<{
  name: string;
  iconName: AppIcons;
  options: FilterOption[];
  selectedValues: FilterOption[];
  onSelectionChange: (selectedValues: FilterOption[]) => void;
}>;

export const FilterButton = ({
  name,
  iconName,
  options,
  selectedValues,
  onSelectionChange,
}: FilterButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isAnyItemSelected = selectedValues.length > 0;

  useMemo(() => {
    validateSelections(selectedValues, options);
  }, [selectedValues, options]);

  const handleMenuItemClick = (optionKey: string) => {
    if (selectedValues.some((value) => value.key === optionKey)) {
      onSelectionChange(selectedValues.filter((value) => value.key !== optionKey));
    } else {
      const optionToAdd = options.find((option) => option.key === optionKey);

      if (optionToAdd) {
        onSelectionChange([...selectedValues, optionToAdd]);
      }
    }
  };

  return (
    <Select
      multiple
      value={selectedValues}
      displayEmpty
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        PaperProps: {
          sx: menuPaperPropsSx,
        },
      }}
      onChange={() => null} // changes are handled with the onClick
      sx={selectStyle(isAnyItemSelected)}
      renderValue={() => (
        <Box sx={renderValueStyle}>
          <Box sx={renderValueIconStyle}>
            <AppIcon
              iconName={iconName}
              diameter="20px"
              color={
                isAnyItemSelected ? 'Colors/Text/text-primary' : 'Colors/Text/text-placeholder'
              }
            />
          </Box>
          <Typography
            variant="text-md-medium"
            color={({ backrs }) =>
              isAnyItemSelected
                ? backrs.colors['Colors/Text/text-primary']
                : backrs.colors['Colors/Text/text-placeholder']
            }
          >
            {name}
          </Typography>
          <Box sx={renderValueIconStyle}>
            <AppIcon
              iconName="ChevronDown"
              diameter="20px"
              color={
                isAnyItemSelected ? 'Colors/Text/text-primary' : 'Colors/Text/text-placeholder'
              }
            />
          </Box>
        </Box>
      )}
    >
      {options.map((option) => {
        const isSelected = selectedValues.some((value) => value.key === option.key);

        return (
          <MenuItem
            key={option.key}
            value={option.key}
            disableRipple
            disableTouchRipple
            onClick={() => handleMenuItemClick(option.key)}
          >
            <Box sx={menuItemContentStyle}>
              <Box sx={menuItemIconStyle(isSelected)}>
                <AppIcon
                  iconName={isSelected ? 'Check' : 'Square'}
                  diameter="16px"
                  color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                />
              </Box>
              <Typography variant="text-md-medium">{option.label}</Typography>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

const selectStyle =
  (isSelected: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${isSelected ? backrs.colors['Colors/Foreground/fg-primary'] : backrs.colors['Colors/Foreground/fg-quaternary']}`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    boxShadow: backrs.shadows['shadow-xs'],
    transition: 'all 0.3s ease-in-out',
    minWidth: '120px',
    '&:hover': {
      border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
      boxShadow: backrs.shadows['shadow-lg'],
    },
    '& .MuiSelect-select': {
      paddingY: backrs.spacing['spacing-sm'],
      paddingX: backrs.spacing['spacing-lg'],
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  });

const renderValueStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  boxSizing: 'border-box',
  width: '100%',
  marginRight: backrs.spacing['spacing-md'],
});

const renderValueIconStyle: SxProps<Theme> = {
  width: '20px',
  height: '20px',
};

const menuItemContentStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const menuItemIconStyle =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });

const menuPaperPropsSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
});
